<template>
	<div
		:class="[
			$style['phone-number-container'],
			{
				[$style['phone-number-container--multiple']]: multiple,
				[$style['phone-number-container--dropdown-open']]: isDropdownOpen
			}
		]"
	>
		<div
			:class="[
				$style['phone-number'],
				$style['phone-number--dense'],
				{
					[$style['phone-number--invalid']]: invalid,
					[$style['phone-number--disabled']]: isDisabled,
					[$style['phone-number--focused']]: isFocused
				}
			]"
		>
			<div
				:class="$style['phone-number__flag-container']"
				@click="toggleDropdown"
			>
				<div :class="$style['phone-number__current-flag']">
					<Flag :code="activeCountry && activeCountry.iso2" />
				</div>
			</div>

			<input
				ref="phone"
				v-bind="$attrs"
				:class="$style['phone-number__input']"
				:value="displayPhoneNumber"
				type="tel"
				:disabled="isDisabled"
				@input="onFieldInput"
				@focus="onFieldFocus"
				@blur="onFieldBlur"
			/>

			<FloatingLabel
				:class="$style['phone-number__label']"
				:label-text="labelText"
				:required="isRequired"
				:is-focused="isFocused"
				:is-floated="isFloated"
				:with-left-spacing="!isFloated"
			/>

			<NotchedOutline
				:class="$style['phone-number__outline']"
				:is-focused="isFocused"
				:is-floated="isFloated && !!labelText"
			/>

			<NotchedOutlineIdle
				:class="$style['phone-number__idle']"
				:is-focused="isFocused"
				:is-floated="isFloated && !!labelText"
			/>

			<DropdownMenu
				ref="dropdown"
				:class="$style['phone-number__flag-dropdown']"
				:handle-offset-click="false"
				@close="handleDropdownClose"
				@offset:click="onDropdownOffsetClick"
			>
				<div :class="$style['phone-number__search']">
					<input
						ref="input"
						v-model="search"
						:class="$style['phone-number__search-input']"
						type="text"
						:placeholder="$t('common.search')"
					/>
				</div>

				<ul :class="$style['phone-number__flag-dropdown-list']">
					<li
						v-for="country in countries"
						:key="country.iso2"
						:class="$style['phone-number__flag-dropdown-item']"
						@click="onCountryClick(country)"
					>
						<Flag :code="country.iso2" />
						<small> (+{{ country.dialCode }})</small>
						<span>{{ country.name }}</span>
					</li>
				</ul>
			</DropdownMenu>
		</div>
		<p
			v-if="helperText"
			:class="$style['phone-number-helper-text']"
		>
			{{ helperText }}
		</p>
	</div>
</template>

<script>
import NotchedOutline from '@c:essentials/NotchedOutline'
import NotchedOutlineIdle from '@c:essentials/NotchedOutlineIdle'
import FloatingLabel from '@c:essentials/FloatingLabel'
import DropdownMenu from '@c:essentials/DropdownMenu'
import Flag from './Flag'
import countriesJSON from './countries.json'
import { parsePhoneNumber } from 'awesome-phonenumber'
import requiredField from '@/mixins/requiredField'

export default {
	name: 'PhoneNumberField',
	components: {
		NotchedOutline,
		NotchedOutlineIdle,
		FloatingLabel,
		DropdownMenu,
		Flag
	},
	mixins: [requiredField],
	inheritAttrs: false,
	props: {
		maxLength: {
			type: [String, Number],
			default: ''
		},
		icon: {
			type: [String, Number],
			default: ''
		},
		placeholder: {
			type: [String, Number],
			default: ''
		},
		value: {
			type: String,
			default: null
		},
		labelText: {
			type: String,
			default: ''
		},
		required: {
			type: [Boolean, String],
			default: false
		},
		helperText: {
			type: String,
			default: null
		},
		multiple: {
			type: [Boolean, String],
			default: false
		},
		invalid: {
			type: [Boolean, String],
			default: false
		},
		defaultCountry: {
			type: String,
			default: null
		}
	},
	data () {
		return {
			isFocused: false,
			isFloated: false,
			activeCountry: null,
			search: null,
			inputValue: null,
			isDropdownOpen: false
		}
	},
	computed: {
		isDisabled () {
			return this.$attrs.disabled === '' || this.$attrs.disabled
		},

		countries () {
			if (this.search) {
				const normalize = val => val.normalize('NFKD').replace(/[^\w\s]/g, '')
				const loweredInput = typeof this.search === 'string' ? this.search.toLowerCase() : ''

				return countriesJSON.filter(country => {
					const loweredValue = typeof country.name === 'string' ? country.name.toLowerCase() : ''
					const formatedInput = normalize(loweredInput).trim().replace(/ /g, '')
					const formatedValue = normalize(loweredValue).replace(/ /g, '')
					return formatedValue.includes(formatedInput)
				}).sort((a, _b) => {
					const loweredValue = typeof a.name === 'string' ? a.name.toLowerCase() : ''
					return normalize(loweredValue).includes(normalize(loweredInput)) ? -1 : 0
				})
			}
			return countriesJSON
		},

		displayPhoneNumber () {
			if (this.computedPhoneNumber) {
				try {
					// Only format when strictly valid - break formatting when possible but not valid
					if (this.isPhoneValid(this.computedPhoneNumber)) {
						// Use international format with spaces (e.g., "+33 6 92 51 18 16")
						return this.getPhoneNumber(this.computedPhoneNumber, 'international') || this.inputValue || ''
					} else {
						// Break formatting - show raw input for possible/invalid numbers
						return this.inputValue || ''
					}
				} catch (error) {
					console.warn('Error displaying phone number:', error)
					return this.inputValue || ''
				}
			}
			return this.inputValue || ''
		},

		computedPhoneNumber () {
			try {
				if (this.activeCountry && this.inputValue) {
					// Ensure we pass valid parameters to parsePhoneNumber
					const countryCode = this.activeCountry.iso2 ? this.activeCountry.iso2.toUpperCase() : null
					if (countryCode && this.inputValue) {
						return parsePhoneNumber(this.inputValue, { regionCode: countryCode })
					}
				}

				if (this.inputValue) {
					// Handle case where input might already contain country code
					return parsePhoneNumber(this.inputValue)
				}

				return null
			} catch (error) {
				console.warn('PhoneNumber parsing error:', error, {
					inputValue: this.inputValue,
					activeCountry: this.activeCountry
				})
				return null
			}
		}
	},
	watch: {
		value: {
			immediate: true,
			handler (newValue) {
				if (newValue !== null && newValue !== '') {
					this.isFloated = true
					// Sanitize: remove all spaces and non-digit chars except +
					const sanitized = typeof newValue === 'string'
						? newValue.replace(/[^0-9+]/g, '')
						: newValue
					// Only update inputValue if it's different from current sanitized value
					if (this.inputValue !== sanitized) {
						this.inputValue = sanitized
						// Try to detect country from the phone number
						this.detectCountryFromValue(sanitized)
					}
				} else if (!this.isFocused) {
					if (this.activeCountry && this.activeCountry.dialCode) {
						this.inputValue = `+${this.activeCountry.dialCode}`
						this.isFloated = true
					} else {
						this.isFloated = false
						this.inputValue = null
					}
				}
			}
		},
		defaultCountry: {
			immediate: false,
			handler (country) {
				if (!country) {
					this.activeCountry = null
					if (!this.value) {
						this.inputValue = null
						this.isFloated = false
					}
					return
				}

				const foundCountry = this.findCountry(country)
				if (!foundCountry) {
					return
				}

				// If the same country is already active, don't reset the existing number
				if (this.activeCountry && this.activeCountry.iso2 === foundCountry.iso2) {
					return
				}

				this.onCountryClick(foundCountry)
			}
		}
	},
	created () {
		this.initializeComponent()
	},
	methods: {
		initializeComponent () {
			// Handle initial value setup
			if (this.value !== null && this.value.length) {
				this.isFloated = true
				this.inputValue = this.value
				this.detectCountryFromValue(this.value)
			} else if (this.defaultCountry) {
				const foundCountry = this.findCountry(this.defaultCountry)
				if (foundCountry && foundCountry.dialCode) {
					this.activeCountry = foundCountry
					this.isFloated = true
					this.inputValue = `+${foundCountry.dialCode}`
				}
			}
		},

		detectCountryFromValue (value) {
			try {
				const phoneNumber = parsePhoneNumber(value)
				const regionCode = this.getPhoneRegionCode(phoneNumber)
				if (regionCode) {
					const foundCountry = this.findCountry(regionCode)
					if (foundCountry ) {
						this.activeCountry = foundCountry
						if (!this.value) {
							this.inputValue = `+${foundCountry.dialCode}`
						}
					}
				}
			} catch (error) {
				console.warn('Error detecting country from value:', error)
			}
		},

		onCountryClick (country) {
			// Add validation
			if (!country || !country.dialCode) {
				console.warn('Invalid country selected:', country)
				return
			}

			const previousInput = this.inputValue
			const previousCountry = this.activeCountry

			// Capture the parsed region *before* we change activeCountry, otherwise the computed
			// property will re-parse using the new country and may short-circuit the reformatting.
			const parsed = this.computedPhoneNumber
			const currentRegion = parsed ? this.getPhoneRegionCode(parsed) : null

			this.activeCountry = country
			this.$refs.dropdown?.close()
			this.isDropdownOpen = false

			if (!previousInput) {
				this.isFloated = true
				this.inputValue = `+${country.dialCode}`
				this.$emit('input', this.inputValue)
				return
			}

			// If the selected country is the same as the one already parsed, keep the value
			if (currentRegion && currentRegion.toLowerCase() === country.iso2.toLowerCase()) {
				return
			}

			// Try to keep the national number and reformat with the new dial code
			let nationalNumber = ''
			if (previousCountry) {
				try {
					const previousParsed = parsePhoneNumber(previousInput, { regionCode: previousCountry.iso2.toUpperCase() })
					nationalNumber = this.getPhoneNumber(previousParsed, 'significant')
				} catch (error) {
					// ignore
				}
			}

			if (!nationalNumber) {
				try {
					const parsedFallback = parsePhoneNumber(previousInput)
					nationalNumber = this.getPhoneNumber(parsedFallback, 'significant')
				} catch (error) {
					// ignore
				}
			}

			if (nationalNumber) {
				this.inputValue = `+${country.dialCode}${nationalNumber}`
				this.$emit('input', this.inputValue)
				return
			}

			if (previousInput.charAt(0) !== '+') {
				this.inputValue = `+${country.dialCode}${previousInput}`
				this.$emit('input', this.inputValue)
				return
			}

			// If the input is only the previous dial code, replace it with the new one
			if (previousInput === '+' || (previousCountry && previousInput === `+${previousCountry.dialCode}`)) {
				this.inputValue = `+${country.dialCode}`
				this.$emit('input', this.inputValue)
				return
			}

			// Could not parse the existing number; keep it and just update the flag
			this.isFloated = true
		},

		onFieldInput (event) {
			event.preventDefault()

			// Strip all non-digit and non-+ characters (including spaces)
			let value = event.target.value.replace(/[^0-9+]/g, '')
			event.target.value = value
			this.inputValue = value

			try {
				// Auto-correct common formatting issues
				if (this.computedPhoneNumber) {
					// If invalid but has a leading 0 after country code, try removing it
					if (!this.isPhoneValid(this.computedPhoneNumber) && !this.isPhonePossible(this.computedPhoneNumber) && this.inputValue) {
						const corrected = this.inputValue.replace(/^(\+\d{1,3})0(\d{8,})/, '$1$2')
						if (corrected !== this.inputValue) {
							const testNumber = parsePhoneNumber(corrected)
							if (testNumber && (this.isPhoneValid(testNumber) || this.isPhonePossible(testNumber))) {
								this.inputValue = corrected
								value = corrected
								event.target.value = corrected
							}
						}
					}

					const regionCode = this.computedPhoneNumber ? this.getPhoneRegionCode(this.computedPhoneNumber) : null
					if (regionCode) {
						const foundCountry = this.findCountry(regionCode)
						if (foundCountry) {
							this.activeCountry = foundCountry
						}
					}

					if (this.computedPhoneNumber && (this.isPhoneValid(this.computedPhoneNumber) || this.isPhonePossible(this.computedPhoneNumber))) {
						this.$emit('valid')
						const num = this.getPhoneNumber(this.computedPhoneNumber)
						value = typeof num === 'string' ? num : this.inputValue
					}
				}
			} catch (error) {
				console.warn('Error processing phone input:', error)
			}

			this.$emit('input', value)
		},

		onFieldFocus (event) {
			this.isFocused = true
			this.isFloated = true
			this.$refs.dropdown.close()
			this.isDropdownOpen = false
			this.$emit('focus', event)
		},

		onFieldBlur (event) {
			if (!this.isFocused) return

			this.isFocused = false
			this.isFloated = !!(this.value || this.placeholder || this.inputValue)

			this.$emit('blur', event)
		},

		onDropdownOffsetClick (e) {
			if (e.target !== this.$el && !this.$el.contains(e.target)) {
				this.$refs.dropdown.close()
				this.isDropdownOpen = false
			}
		},

		toggleDropdown () {
			if (this.isDisabled) return
			this.$refs.dropdown.toggle()
			this.isDropdownOpen = !!this.$refs.dropdown?.isOpen
		},

		handleDropdownClose () {
			this.isDropdownOpen = false
		},

		findCountry (iso = '') {
			return this.countries.find((country) => country.iso2 === iso.toLowerCase())
		},

		isValid () {
			try {
				if (this.computedPhoneNumber) {
					// Accept both valid and possible numbers
					let isValid = this.isPhoneValid(this.computedPhoneNumber) || this.isPhonePossible(this.computedPhoneNumber)
					let num = this.getPhoneNumber(this.computedPhoneNumber)

					// If still invalid, try removing leading 0 after country code
					if (!isValid && this.inputValue) {
						const corrected = this.inputValue.replace(/^(\+\d{1,3})0(\d{8,})/, '$1$2')
						if (corrected !== this.inputValue) {
							const testNumber = parsePhoneNumber(corrected)
							if (testNumber && (this.isPhoneValid(testNumber) || this.isPhonePossible(testNumber))) {
								isValid = true
								num = this.getPhoneNumber(testNumber)
							}
						}
					}

					if (isValid) {
						const numStr = typeof num === 'string' ? num : this.inputValue
						if (this.value !== numStr) {
							this.$emit('input', numStr)
						}
					}
					return isValid
				}
				return false
			} catch (error) {
				console.warn('Error validating phone number:', error)
				return false
			}
		},

		getPhoneRegionCode (phoneNumber) {
			if (!phoneNumber) return null

			if (typeof phoneNumber.getRegionCode === 'function') {
				return phoneNumber.getRegionCode()
			}

			return phoneNumber.regionCode || null
		},

		isPhoneValid (phoneNumber) {
			if (!phoneNumber) return false

			if (typeof phoneNumber.isValid === 'function') {
				return phoneNumber.isValid()
			}

			return Boolean(phoneNumber.valid)
		},

		isPhonePossible (phoneNumber) {
			if (!phoneNumber) return false

			if (typeof phoneNumber.isPossible === 'function') {
				return phoneNumber.isPossible()
			}

			return Boolean(phoneNumber.possible)
		},

		getPhoneNumber (phoneNumber, format = null) {
			if (!phoneNumber) return ''

			if (typeof phoneNumber.getNumber === 'function') {
				return phoneNumber.getNumber(format || undefined) || ''
			}

			const number = phoneNumber.number || {}

			if (format === 'international') return number.international || number.e164 || ''
			if (format === 'significant') return number.significant || ''
			if (format === 'input') return number.input || this.inputValue || ''

			return number.e164 || number.international || number.national || number.input || this.inputValue || ''
		}
	}
}
</script>
<style lang="scss" module>
.phone-number-container {
    position: relative;
    width: 100%;

    &--dropdown-open {
        z-index: 10;
    }

    &--multiple {
        padding: 0 $gutter * 1.25;
    }
}

.phone-number-helper-text {
    display: flex;
    align-items: center;
    margin: 0;
    margin-bottom: calc($gutter / 2);
    color: getColor(helper-text);
    font-size: 12px;
    line-height: 1.25rem;
    width: 100%;
    position: relative;

    &--right {
        text-align: right;
        justify-self: flex-end;
        flex: 1;
    }
}

.phone-number {
    position: relative;
    display: inline-flex;
    width: 100%;
    align-items: flex-end;
    box-sizing: border-box;
    will-change: opacity, transform, color;
    margin: $gutter * 1.25 0;
    height: 56px;
    border: none;
    border-radius: 4px;
    z-index: 2;
    background-color: getColor(input-background);

    &__label {
        z-index: 1;
    }

    &__idle,
    &__outline {
        z-index: 1;
    }

    &__flag-container {
        z-index: 0;
        height: calc(100% - 2px);
        display: flex;
        align-items: center;
        padding: 0 20px;
        padding-right: 10px;
        align-self: flex-start;
        border-radius: 4px 0 0 4px;

        &:hover {
            cursor: pointer;
            background-color: getColor(hover-overlay);
        }
    }

    &__current-flag {
        padding-right: 20px;

        &::before {
            content: '';
            width: 10px;
            height: 5px;
            position: absolute;
            left: 50px;
            top: 50%;
            transform: translateY(-50%);
            background-color: getColor(grey);
            mask-image: url("data:image/svg+xml;charset=utf-8,%3Csvg width='10' height='5' viewBox='7 10 10 5' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' d='M7 10l5 5 5-5z'/%3E%3C/svg%3E");
            mask-size: contain;
            mask-repeat: no-repeat;
            mask-position: center;
            transition: transform 0.2s ease-in-out;
        }

        .phone-number-container--dropdown-open &::before {
            transform: translateY(-50%) rotate(-180deg);
        }
    }

    &__flag-dropdown {
        padding: 20px 0;
        z-index: 3;
        border-radius: 6px;
        left: 0;
        top: 100%;
        transform-origin: top left;
        max-height: 400px;
        overflow: auto;
        width: 100%;
    }

    &__flag-dropdown-list {
        list-style: none;
        padding: 0;
        margin: 0;
    }

    &__flag-dropdown-item {
        display: flex;
        align-items: center;
        white-space: nowrap;
        padding: 5px 20px;

        &:hover {
            cursor: pointer;
            background-color: getColor(hover-overlay);
        }

        small {
            padding-left: 10px;
            color: getColor(helper-text);
        }

        span {
            padding-left: 10px;
            font-size: 14px;
        }
    }

    &__search {
        display: flex;
        align-items: center;
        padding: 0 20px;
        padding-top: 0;
        padding-bottom: 20px;
        background-color: getColor(surface-elevated);
    }

    &__search-input {
        appearance: none;
        width: 100%;
        padding: 10px 20px;
        border-radius: 4px;
        border: 1px solid getColor(border);
        background-color: getColor(input-background);
        color: getColor(text);
        outline: 0;
        font-size: 14px;

        @include dark-mode-border-fix(border);

        &::placeholder {
            color: getColor(helper-text);
        }
    }

    &__clear {
        position: absolute;
        right: 15px;
        top: 50%;
        transform: translateY(-50%);
        z-index: 3;
        color: getColor(grey);
        font-size: 13px;
    }

    &__input {
        z-index: 2;
        display: flex;
        box-sizing: content-box;
        border: none;
        border-radius: 0;
        background-color: transparent;
        font-size: 16px;
        line-height: 1.75rem;
        font-weight: 400;
        letter-spacing: 0.0094em;
        width: 100%;
        height: 30px;
        transition: opacity 0.18s cubic-bezier(0.4, 0, 0.2, 1);
        caret-color: getColor(tertiary);

        &:focus {
            outline: none;
        }

        &:not(:focus, :disabled):hover {
            & ~ .phone-number__outline {
                border-color: getColor(text);
            }

            & ~ .phone-number__idle {
                border-color: getColor(text);
            }
        }

        &:-webkit-autofill,
        &:-webkit-autofill:hover,
        &:-webkit-autofill:focus,
        &:-webkit-autofill:active {
            -webkit-text-fill-color: getColor(text) !important;
            box-shadow: 0 0 0 1000px transparent inset !important;
            transition: opacity 0.18s cubic-bezier(0.4, 0, 0.2, 1), background-color 5000s ease-in-out 0s;
        }

        &[type="number"]::-webkit-inner-spin-button,
        &[type="number"]::-webkit-outer-spin-button {
            appearance: none;
            margin: 0;
        }
    }

    &:not(.phone-number--disabled) {
        & .phone-number__idle {
            background-color: transparent;
        }

        & .phone-number__outline {
            background-color: transparent;
        }

        .phone-number__input {
            color: getColor(text);
        }
    }

    &--dense {
        height: 48px;

        .phone-number__input {
            padding: $gutter $gutter * 2;
            padding-left: 0;
        }
    }

    &--no-helper {
        margin-bottom: $gutter * 1.25;
    }

    &--disabled {
        background-color: getColor(input-background-disabled);

        .phone-number__idle {
            background-color: transparent;
        }

        .phone-number__outline {
            background-color: transparent;
        }

        .phone-number__label {
            color: getColor(input-label-disabled);
        }

        .phone-number__input {
            color: getColor(helper-text);
        }

        .phone-number__flag-container {
            &:hover {
                cursor: not-allowed;
                background-color: transparent;
            }
        }
    }

    &--invalid {
        .phone-number__input {
            caret-color: getColor(danger);
        }

        .phone-number__outline {
            border-color: getColor(danger) !important;
        }

        .phone-number__idle {
            border-color: getColor(danger) !important;
        }

        .phone-number__label {
            color: getColor(input-label-disabled) !important;
        }

        & ~ .phone-number-helper-text {
            color: getColor(danger) !important;
        }
    }
}

</style>
